import {
    filter,
    find,
    findIndex,
    has,
    includes,
    map,
    reduce,
    startsWith,
    sumBy,
    upperFirst,
    some,
    indexOf
} from 'lodash';

import { graphqlFetch, stripTags } from '~/utilities/helpers';
import { formatPrice, getProductPrice } from '~/utilities/products';
import { validatePromoCode } from '~/utilities/promos';
import { isLoggedIn } from '~/account/utilities/helpers';

import giftCards from '~/account/api/giftCards';
import subscriptions from '~/account/api/subscriptions';
import physicalProductsApi from '~/account/api/physicalProducts';
import washes from '~/account/api/washes';
import prepaidWashes from '~/account/api/prepaidWashes';

import AmexLogo from '~/images/credit-cards/amex.svg';
import DiscoverLogo from '~/images/credit-cards/discover.png';
import MastercardLogo from '~/images/credit-cards/mastercard.svg';
import VisaLogo from '~/images/credit-cards/visa.png';
import DefaultCard from '~/images/credit-cards/default.svg';

const getProductFromCMS = async (id, location, displayName = null) => {
    const query = `query {
            product${displayName ? `s(where: {displayName: "${displayName}"})` : `(id: "${id}", idType: DATABASE_ID)`} {
                ${displayName ? `nodes {` : ''}
                    ampId
                    databaseId
                    title
                    displayName
                    locations
                    featuredImage {
                        node {
                            sourceUrl
                        }
                    }
                    productInformation {
                        price
                        showVehicleInformationForm
                        bogo {
							discountAmount
							discountType
							enableBogo
							threshold
							prepaidPromotion{
								... on PrepaidPromotion {
									title
									subTitle
									ampId
									effectiveFrom
									effectiveTo
									ordinal
									percentOff
									price
									promotionQuantity
									purchaseLimit
									purchaseQuantity
									savingsLabel
									washTypeIds
								}
							}
						}
                    }
                    stripeId
                    stripePrices(priceGroup: ${location.priceGroup}) {
                        priceGroup
                        label
                        stripeId
                        id
                        unitAmount
                        interval
                        interval_count
                        ordinal
                    }
                    productTypes {
                        nodes {
                            slug
                        }
                    }
                    cleaningCategories {
                        nodes {
                            slug
                        }
                    }
                ${displayName ? `}` : ''}                
            }
        }`;

    const { products, product: graphQLProduct } = await graphqlFetch(query);
    let product = null;
    if (products) {
        product = find(products.nodes, ({ locations }) =>
            includes(locations, location.ampID)
        );
    } else if (graphQLProduct) {
        product = graphQLProduct;
    }
    return product;
};

export const queryCartItem = async (
    id,
    location,
    displayName = null,
    giftCardValue = null
) => {
    const product = await getProductFromCMS(id, location, displayName);
    if (product) {
        const productType = product.productTypes.nodes[0].slug;
        const stripePrice =
            productType === 'subscription'
                ? find(product.stripePrices, {
                      priceGroup: location.priceGroup,
                      interval_count: 1,
                      interval: 'month'
                  })
                : product.stripePrices[0];

        return {
            ampId: product?.ampId,
            id: product.databaseId,
            location: {
                id: location.ampID,
                address: location.address
            },
            title: product.displayName ? product.displayName : product.title,
            price:
                giftCardValue ??
                getProductPrice(
                    product,
                    productType,
                    location.priceGroup,
                    false
                ),
            bogoThreshold: product.productInformation.bogo.enableBogo
                ? product.productInformation.bogo.threshold
                : undefined,
            bogoDiscountType: product.productInformation.bogo.enableBogo
                ? product.productInformation.bogo.discountType
                : undefined,
            bogoDiscountAmount: product.productInformation.bogo.enableBogo
                ? product.productInformation.bogo.discountAmount
                : undefined,
            bogoPrepaidPromotion: product.productInformation.bogo
                .prepaidPromotion
                ? product.productInformation.bogo.prepaidPromotion
                : undefined,
            stripe_id: product.stripeId,
            stripe_price_id: stripePrice?.stripeId,
            price_id: stripePrice?.id,
            image: product.featuredImage?.node.sourceUrl,
            productType,
            vehicle: {
                garage: ''
            },
            showVehicleInformationForm:
                !!product?.productInformation?.showVehicleInformationForm,
            cleaningCategories: product?.cleaningCategories?.nodes || []
        };
    }
    return null;
};

export const createGtagEcommEvent = (
    eventType,
    items,
    additionalValues = {}
) => {
    if (window.dataLayer) {
        const gtagItems = reduce(
            items,
            (acc, item) => {
                const gtagAddons = map(item.addOns, (addOn) => ({
                    item_id: addOn.id,
                    item_name: addOn.title,
                    quantity: item.quantity,
                    price: addOn.price / 100
                }));
                return [
                    ...acc,
                    {
                        item_id: item.id,
                        item_name: item.title,
                        quantity: item.quantity,
                        price: item.price / 100
                    },
                    ...gtagAddons
                ];
            },
            []
        );
        const eventData = {
            value: sumBy(gtagItems, ({ price }) => price * 100) / 100,
            currency: 'USD',
            items: gtagItems,
            ...additionalValues
        };
        window.dataLayer.push({
            event: eventType,
            ecommerce: eventData
        });
    }
};

export const getBogoItems = (
    threshold,
    bogoDiscountType,
    bogoDiscountAmount,
    quantity,
    cartItemId,
    cartItems
) => {
    const bogoItemIndex = findIndex(cartItems, { cartItemId });
    const bogoItem = cartItems[bogoItemIndex];
    cartItems = cartItems.filter(
        ({ cartItemId: itemId }) => !startsWith(itemId, `${cartItemId}-bogo`)
    );

    if (
        quantity >= threshold ||
        (bogoItem.productType === 'digital' && cartItems.length >= threshold)
    ) {
        const totalItemsToAdd =
            bogoItem.productType === 'digital'
                ? Math.floor(quantity / threshold)
                : Math.floor(quantity / threshold);
        let bogoItems = [];
        let price = calculateBogoPrice(
            bogoDiscountType,
            bogoDiscountAmount,
            bogoItem
        );

        for (let i = 0; i < totalItemsToAdd; i++) {
            bogoItems.push({
                ...bogoItem,
                title: bogoItem.bogoPrepaidPromotion
                    ? `${bogoItem.title} - ${bogoItem.bogoPrepaidPromotion.title}`
                    : `${bogoItem.title} - BOGO`,
                quantity: 1,
                bogoItem: true,
                price: price,
                tax: 0,
                priceDisplay: price > 0 ? `$${price}` : 'FREE',
                cartItemId: `${cartItemId}-bogo-${i}`
            });
        }

        cartItems = cartItems.toSpliced(bogoItemIndex + 1, 0, ...bogoItems);
    }
    return cartItems;
};

const calculateBogoPrice = (bogoDiscountType, bogoDiscountAmount, bogoItem) => {
    let finalPrice = 0,
        itemPrice = bogoItem.price / 100,
        price,
        prepaidPromo = bogoItem.bogoPrepaidPromotion,
        prepaidFlat = prepaidPromo
            ? prepaidPromo.price !== null
                ? true
                : false
            : false;
    if (prepaidPromo) {
        switch (prepaidFlat) {
            // discount flat dollar amount
            case true:
                price = itemPrice - prepaidPromo.price;
                break;
            // discount percentage
            case false:
                price = itemPrice - itemPrice * (prepaidPromo.percentOff / 100);
                break;
            default:
                break;
        }
    } else {
        switch (bogoDiscountType) {
            case 'percent':
                price = itemPrice - itemPrice * (bogoDiscountAmount / 100);
                break;
            case 'dollar':
                price = itemPrice - bogoDiscountAmount;
                break;
            default:
                break;
        }
    }

    finalPrice = price >= 0 ? price : 0;
    return Math.round(finalPrice * 100) / 100;
};

export const canGuestCheckout = (items) => {
    return (
        items.length &&
        !some(items, ({ productType }) =>
            includes(['subscription', 'digital'], productType)
        )
    );
};

const createPhysicalProductsDraftInvoice = async (
    customerId,
    items,
    promoCode = null
) => {
    const physicalItemsInvoiceID = find(items, 'invoice_id')?.invoice_id;
    const response =
        await physicalProductsApi.createPhysicalProductsDraftInvoice(
            customerId,
            items,
            promoCode,
            physicalItemsInvoiceID
        );
    if (response.success) {
        let subtotal = response.data.subtotal;
        if (response.data.discount) {
            subtotal = subtotal + response.data.discount;
        }
        return {
            invoice_id: response.data.invoice_id,
            total: response.data.total,
            subTotal: subtotal,
            tax: response.data.tax,
            discount: response.data.discount,
            invoice: response.data,
            refreshKey: response.refreshKey
        };
    }
    return {
        error: response.errorCode
    };
};

const validateDiscounts = async (promoCodes, items, customerId) => {
    let physicalProductsInvoice = null;
    let promoCodeErrors = [];
    let promoCodesWithIDs = [];
    let promoCodeErrorMsg = '';
    let newItems = [...items];

    const subscriptionItems = filter(newItems, {
        productType: 'subscription'
    });

    const physicalProducts = filter(newItems, { productType: 'physical' });

    const digitalProducts = filter(newItems, { productType: 'digital' });

    for (const promoCode of promoCodes) {
        const { code } = promoCode;
        let didSucceed = false;
        let codeId = null;

        const { successItems, promoCodeId } = await validatePromoCode(
            code,
            customerId,
            subscriptionItems
        );

        if (successItems.length) {
            for (const item of successItems) {
                const { cartItemId, promoCodeId } = item;
                if (cartItemId) {
                    const newItemIndex = findIndex(newItems, {
                        cartItemId
                    });
                    newItems[newItemIndex].promoCode = {
                        id: promoCodeId,
                        code
                    };
                }
            }
            //Prevent double application of promo codes
            for (const item of physicalProducts) {
                const newItemIndex = findIndex(newItems, {
                    cartItemId: item.cartItemId
                });
                delete newItems[newItemIndex].promoCode;
            }
            didSucceed = true;
            codeId = promoCodeId;
        }

        if (physicalProducts.length && !didSucceed) {
            const response = await createPhysicalProductsDraftInvoice(
                customerId,
                physicalProducts,
                code
            );

            if (!response.error) {
                physicalProductsInvoice = response;
                for (const item of physicalProducts) {
                    const newItemIndex = findIndex(newItems, {
                        cartItemId: item.cartItemId
                    });
                    newItems[newItemIndex].promoCode = {
                        id: response.promo_code_id,
                        code
                    };
                }
                didSucceed = true;
                codeId = response.promo_code_id;
            }
        }

        if (digitalProducts.length && !didSucceed) {
            for (const item of digitalProducts) {
                if (item.bogoPrepaidPromotion) continue;
                if (!has(item, 'vehicle')) {
                    promoCodeErrorMsg = `You must select a vehicle first before you can add a promotional code for ${item.title}`;
                    continue;
                }

                const { digitalItem } = await washes.addInvoice(
                    item.location.id,
                    item,
                    code
                );

                const { success, value } = await washes.addPromoCode(
                    code,
                    item.location.id,
                    digitalItem
                );

                const newItemIndex = findIndex(newItems, {
                    cartItemId: digitalItem.cartItemId
                });

                if (success && newItemIndex > -1) {
                    newItems[newItemIndex] = value;
                    didSucceed = true;
                } else if (newItemIndex > -1) {
                    newItems[newItemIndex] = digitalItem;
                }
            }
        }

        if (didSucceed) {
            promoCodesWithIDs.push({
                code,
                id: codeId
            });
        } else {
            promoCodeErrors.push(code);
        }
    }

    if (physicalProducts.length && !physicalProductsInvoice) {
        const response = await createPhysicalProductsDraftInvoice(
            customerId,
            physicalProducts
        );

        if (!response.error) {
            physicalProductsInvoice = response;
        }
    }

    const addInvoiceInfoForPhysicalProduct = () => {
        return map(newItems, (item) => {
            if (item?.productType === 'physical') {
                const physicalTotal =
                    (item.price + sumBy(item.addOns, 'price')) * item.quantity;

                const fallback = {
                    subTotal: physicalTotal,
                    total: physicalTotal,
                    discount: 0,
                    tax: 0
                };

                const physicalInvoice = physicalProductsInvoice ?? fallback;

                return {
                    ...item,
                    invoice_id: physicalInvoice.invoice_id
                };
            }

            return item;
        });
    };

    return {
        promoCodeErrorMsg,
        promoCodeErrors,
        physicalProductsInvoice,
        promoCodes: promoCodesWithIDs,
        items: addInvoiceInfoForPhysicalProduct()
    };
};

export const getTotals = async (items, customerId, discounts = []) => {
    const {
        items: newItems,
        physicalProductsInvoice,
        promoCodeErrors,
        promoCodes,
        promoCodeErrorMsg
    } = await validateDiscounts(discounts, items, customerId);

    let bogoCounter = 0;
    let prepaidInvoices = {};

    const totals = await reduce(
        [...newItems],
        async (totals, item, index) => {
            let awaitedTotals = await totals;
            const isBogo = item?.cartItemId?.includes('bogo');

            switch (item.productType) {
                case 'gift-card':
                    const { giftItem } = await giftCards.getInvoice(
                        item,
                        item.location.id
                    );

                    const giftDiscount = giftItem.discount ?? 0;
                    const giftTax = giftItem.tax ?? 0;
                    const giftSubTotal = giftItem.subtotal ?? item.price;
                    const giftTotal = giftItem.total ?? item.price;

                    awaitedTotals.discount += giftDiscount;
                    awaitedTotals.tax += giftTax * item.quantity;
                    awaitedTotals.subTotal += giftSubTotal * item.quantity;
                    awaitedTotals.grandTotal += giftTotal * item.quantity;

                    awaitedTotals.items[index] = giftItem;

                    return awaitedTotals;
                case 'subscription':
                    const { subscriptionItem } =
                        await subscriptions.previewNewSubscriptionInvoice(
                            item,
                            item.location.id
                        );

                    const subscriptionInvoice = subscriptionItem.invoice;
                    const subscriptionTax = subscriptionInvoice?.tax ?? 0;
                    const subscriptionDiscount =
                        subscriptionInvoice?.discount ?? 0;
                    const subscriptionSubTotal =
                        subscriptionInvoice?.subtotal ?? item.price;
                    const subscriptionGrandTotal =
                        subscriptionInvoice?.total ??
                        item.price + subscriptionTax;

                    awaitedTotals.tax += subscriptionTax;
                    awaitedTotals.discount += subscriptionDiscount;
                    awaitedTotals.subTotal += subscriptionSubTotal;
                    awaitedTotals.grandTotal += subscriptionGrandTotal;

                    awaitedTotals.items[index] = subscriptionItem;

                    return awaitedTotals;
                case 'physical':
                    if (!physicalProductsInvoice) {
                        awaitedTotals.discount += item.discount;
                        awaitedTotals.tax += item.tax;
                        awaitedTotals.subTotal += item.subTotal;
                        awaitedTotals.grandTotal += item.total;
                    }

                    awaitedTotals.items[index] = item;

                    return awaitedTotals;
                case 'digital':
                    if (isLoggedIn() && item.bogoPrepaidPromotion && !isBogo) {
                        let invoiceItem = find(
                            newItems,
                            (x) => has(x, 'invoice') && x.id === item.id
                        );

                        if (invoiceItem) {
                            if (item.id === invoiceItem.id) {
                                bogoCounter++;
                            }

                            const { success, updatedWash } =
                                await prepaidWashes.updateDraftInvoice(
                                    invoiceItem.location.address.postalCode,
                                    invoiceItem.location.id,
                                    invoiceItem,
                                    bogoCounter
                                );
                            if (success) {
                                const invoiceIndex = indexOf(
                                    awaitedTotals.items,
                                    invoiceItem
                                );
                                prepaidInvoices[updatedWash.invoice.id] =
                                    updatedWash.invoice;

                                awaitedTotals.items[invoiceIndex] = updatedWash;
                            }

                            return awaitedTotals;
                        }

                        bogoCounter++;
                        const { prepaidWash } =
                            await prepaidWashes.createDraftInvoice(
                                item.location.address.postalCode,
                                item.location.id,
                                item
                            );

                        prepaidInvoices[prepaidWash.invoice.id] =
                            prepaidWash.invoice;

                        awaitedTotals.items[index] = prepaidWash;

                        return awaitedTotals;
                    } else if (item.bogoPrepaidPromotion && isBogo) {
                        let invoiceItem = find(
                            newItems,
                            (x) => has(x, 'invoice') && x.id === item.id
                        );

                        if (!invoiceItem) {
                            awaitedTotals.subTotal += item.price * 100;
                            awaitedTotals.grandTotal += item.price * 100;

                            awaitedTotals.items[index] = item;
                        }

                        return awaitedTotals;
                    } else {
                        const { digitalItem, digitalTotal } =
                            await washes.addInvoice(item.location.id, item);

                        const digitalComputedTotal = isBogo
                            ? digitalTotal * 100
                            : digitalTotal;
                        const digitalInvoice = digitalItem.invoice;
                        const digitalTax = digitalInvoice?.tax ?? 0;
                        const digitalDiscount = digitalInvoice?.discount ?? 0;
                        const digitalSubTotal =
                            digitalInvoice?.subtotal ?? digitalComputedTotal;
                        const digitalGrandTotal =
                            digitalInvoice?.total ?? digitalComputedTotal;

                        awaitedTotals.tax += digitalTax;
                        awaitedTotals.discount += digitalDiscount;
                        awaitedTotals.subTotal += digitalSubTotal;
                        awaitedTotals.grandTotal += digitalGrandTotal;

                        awaitedTotals.items[index] = digitalItem;

                        return awaitedTotals;
                    }

                default:
                    console.error(`Unknown product type: ${item?.productType}`);
                    awaitedTotals.subTotal += item?.price * item?.quantity;
                    awaitedTotals.grandTotal += item?.price * item?.quantity;
                    awaitedTotals.items[index] = item ?? {};

                    return awaitedTotals;
            }
        },
        Promise.resolve({
            subTotal: 0,
            tax: 0,
            shipping: 0,
            grandTotal: 0,
            discount: 0,
            items: newItems
        })
    );

    if (physicalProductsInvoice) {
        totals.discount += physicalProductsInvoice.discount ?? 0;
        totals.tax += physicalProductsInvoice.tax ?? 0;
        totals.subTotal += physicalProductsInvoice.subTotal;
        totals.grandTotal += physicalProductsInvoice.total;
    }

    if (Object.keys(prepaidInvoices).length) {
        totals.discount +=
            sumBy(Object.values(prepaidInvoices), 'discount') ?? 0;
        totals.tax += sumBy(Object.values(prepaidInvoices), 'tax') ?? 0;
        totals.subTotal +=
            sumBy(Object.values(prepaidInvoices), (invoice) =>
                Math.trunc(invoice.subtotal)
            ) ?? 0;
        totals.grandTotal +=
            sumBy(Object.values(prepaidInvoices), (invoice) =>
                Math.trunc(invoice.total)
            ) ?? 0;
    }

    if (isLoggedIn()) {
        const {
            data: { balance }
        } = await giftCards.getGiftCardsBalance();
        if (balance) {
            const giftBalance = balance * -1;
            totals.giftCardBalance =
                totals.grandTotal > giftBalance
                    ? giftBalance
                    : totals.grandTotal;
            totals.grandTotal -= totals.giftCardBalance;
        }
    }

    return { ...totals, promoCodeErrors, promoCodes, promoCodeErrorMsg };
};

export const getCreditCardLogo = (brand) => {
    switch (brand) {
        case 'visa':
            return VisaLogo;
        case 'mastercard':
            return MastercardLogo;
        case 'amex':
            return AmexLogo;
        case 'discover':
            return DiscoverLogo;
        default:
            return DefaultCard;
    }
};

export const getCouponErrorMessage = (error) => {
    switch (error) {
        case 'COUPON_NOT_FOUND':
            return 'Coupon not found';
        case 'PURCHASE_TYPE':
            return 'No products in your cart match the criteria for the promo code.';
        default:
            return error;
    }
};

export const getCartErrorMessage = (error, items, refunds) => {
    const { cartItemId, newPrice } = error;
    const cartItem = find(items, { cartItemId });
    const refundObj = find(refunds, { cartItemId });
    const title = stripTags(cartItem?.title || 'the item');

    const addons = cartItem?.addOns ?? [];
    const addonPrice = sumBy(addons, 'price');
    const cartPrice = cartItem?.price ?? 0;
    const tax = cartItem?.tax ?? 0;
    const price = (cartPrice + addonPrice + tax) / 100;
    const priceDisplay = `$${price.toFixed(2)}`;

    let actionMsg = 'Please try again later.';

    if (refundObj) {
        const { refund } = refundObj;
        if (refund?.status === 'succeeded') {
            actionMsg = `We sincerely apologize for the inconvenience caused. Your account was charged ${priceDisplay} and a refund was immediately issued.`;
        } else {
            actionMsg = `We sincerely apologize for the inconvenience caused. Your account was charged ${priceDisplay} but could not be refunded.`;
        }
    }

    switch (error.error) {
        case 'INVOICE_ITEM_NOT_FOUND':
            return `${upperFirst(
                title
            )} is not currently available. Please try again later.`;
        case 'INVOICE_PRICE_MISMATCH':
            return `The price of ${title} has changed to ${formatPrice(
                newPrice
            )} since adding it to your cart. Please confirm the new price before proceeding.`;
        default:
            return `There was an error purchasing the ${title}. ${actionMsg}`;
    }
};
